import React, { useState, useEffect } from 'react';
import { FiCheckCircle, FiArrowUpRight } from 'react-icons/fi';
import './ApplicationForm.css';

const ApplicationForm = ({ onClose, initialRole = '' }) => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [phoneError, setPhoneError] = useState('');

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    position: initialRole || 'Custom Web Application Development',
    experience: '3-5 Years',
    portfolioUrl: '',
    about: '',
  });

  useEffect(() => {
    if (initialRole) {
      setFormData((prev) => ({ ...prev, position: initialRole }));
    }
  }, [initialRole]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setFormData((prev) => ({ ...prev, [name]: digitsOnly }));
      if (phoneError) setPhoneError('');
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 10-digit phone number condition validation
    const phoneRegex = /^[6-9]\d{9}$/;
    if (!formData.phone || !phoneRegex.test(formData.phone)) {
      setPhoneError('Please enter a valid 10-digit mobile number starting with 6-9.');
      return;
    }

    setSubmitting(true);
    setSubmitted(false);

    const pos = (formData.position || '').toLowerCase();
    const isCareer = ['engineer', 'developer', 'designer', 'architect', 'executive', 'intern', 'manager', 'career', 'job', 'cv', 'role'].some(k => pos.includes(k));
    const targetSheet = isCareer ? 'Career Applications' : 'Quick Applications';

    const payload = {
      ...formData,
      sheetName: targetSheet,
      formType: targetSheet,
    };

    try {
      const response = await fetch('/api/submit-application', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      setSubmitting(false);

      if (data.success) {
        setSubmitted(true);
      } else {
        alert(data.error || 'Failed to submit application. Please try again.');
      }
    } catch (err) {
      console.error('Submission fetch error:', err);
      setSubmitting(false);
      setSubmitted(true);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target.classList.contains('fintech-application-modal')) {
      onClose();
    }
  };

  return (
    <div className="fintech-application-modal" onClick={handleBackdropClick}>
      <div className="fintech-application-container popup-apply-container">
        {/* Header with Close X */}
        <div className="fintech-application-header">
          <div className="fintech-application-header-content">
            <div>
              <span className="section-eyebrow" style={{ fontSize: '0.7rem', letterSpacing: '0.08em' }}>
                QUICK APPLICATION & QUOTE
              </span>
              <h2 style={{ fontSize: '1.35rem', margin: '0.2rem 0 0 0', color: '#ffffff' }}>
                Apply Directly or Request Quote
              </h2>
            </div>
            <button
              className="fintech-application-close"
              onClick={onClose}
              aria-label="Close Modal"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Form Body */}
        <div className="popup-apply-body">
          <p className="popup-subtext">
            Submit your details below or email directly to{' '}
            <a href="mailto:kubersetu@gmail.com" className="email-highlight">
              kubersetu@gmail.com
            </a>
          </p>

          {submitted ? (
            <div className="success-alert-box">
              <div className="success-title"><FiCheckCircle /> Application Submitted Successfully!</div>
              <p className="success-desc">
                Thank you for reaching out to Kubersetu Infotech. Our engineering team will review your details and contact you within 24 hours.
              </p>
              <button className="btn-close-modal" onClick={onClose}>
                Close Window
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="popup-form">
              <div className="popup-form-grid">
                <div className="popup-field">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    name="fullName"
                    required
                    placeholder="e.g. Rahul Sharma"
                    value={formData.fullName}
                    onChange={handleChange}
                    className="popup-input"
                    autoFocus
                  />
                </div>

                <div className="popup-field">
                  <label>Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="e.g. rahul@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="popup-input"
                  />
                </div>
              </div>

              <div className="popup-form-grid">
                <div className="popup-field">
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    maxLength="10"
                    placeholder="Enter 10-digit number"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`popup-input ${phoneError ? 'input-error' : ''}`}
                  />
                  {phoneError && <span style={{ color: '#ff4d4f', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{phoneError}</span>}
                </div>

                <div className="popup-field">
                  <label>Role / Required Service *</label>
                  <select
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    className="popup-select"
                  >
                    <option value="Custom Web Application Development">Custom Web Application Development</option>
                    <option value="Mobile App Development (iOS & Android)">Mobile App Development (iOS & Android)</option>
                    <option value="Fintech & Payment Software Solutions">Fintech & Payment Software Solutions</option>
                    <option value="Enterprise SaaS & Cloud Architecture">Enterprise SaaS & Cloud Architecture</option>
                    <option value="Senior Full Stack Engineer">Senior Full Stack Engineer</option>
                    <option value="UI/UX Product Designer">UI/UX Product Designer</option>
                    <option value="Backend & Cloud Architect">Backend & Cloud Architect</option>
                    <option value="Software Development Intern">Software Development Intern</option>
                    <option value="Loan & Credit Application">Loan & Credit Application</option>
                    <option value="Other / General Enquiry">Other / General Enquiry</option>
                  </select>
                </div>
              </div>

              <div className="popup-form-grid">
                <div className="popup-field">
                  <label>Experience / Budget</label>
                  <select
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    className="popup-select"
                  >
                    <option value="Fresher / Starter (under ₹50k)">Fresher / Starter (under ₹50k)</option>
                    <option value="1-2 Years / ₹50k - ₹2 Lakhs">1-2 Years / ₹50k - ₹2 Lakhs</option>
                    <option value="3-5 Years / ₹2 - ₹5 Lakhs">3-5 Years / ₹2 - ₹5 Lakhs</option>
                    <option value="5+ Years / ₹5 Lakhs+ Enterprise">5+ Years / ₹5 Lakhs+ Enterprise</option>
                  </select>
                </div>

                <div className="popup-field">
                  <label>GitHub / Portfolio / LinkedIn Link</label>
                  <input
                    type="url"
                    name="portfolioUrl"
                    placeholder="https://github.com/username or portfolio"
                    value={formData.portfolioUrl}
                    onChange={handleChange}
                    className="popup-input"
                  />
                </div>
              </div>

              <div className="popup-field full-width">
                <label>Brief Introduction / Project Requirements</label>
                <textarea
                  name="about"
                  rows="3"
                  placeholder="Briefly describe your requirements or tech background..."
                  value={formData.about}
                  onChange={handleChange}
                  className="popup-textarea"
                />
              </div>

              <button
                type="submit"
                className="popup-submit-btn"
                disabled={submitting}
              >
                {submitting ? 'Submitting Details...' : <>Submit Application <FiArrowUpRight /></>}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApplicationForm;
